import { Box, Card, CardContent, Typography, TextField, Radio, RadioGroup, FormControlLabel, Stack, Avatar } from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledCard = styled(Card)(({ theme }) => ({
  border: '1px solid #E5E7EB',
  borderRadius: '12px',
  boxShadow: 'none',
  marginBottom: theme.spacing(3),
  backgroundColor: '#FFFFFF'
}));

const PlatformListItem = styled(Box)(({ selected }: { selected?: boolean }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '16px',
  borderRadius: '8px',
  border: `1px solid ${selected ? '#4F46E5' : '#E5E7EB'}`,
  backgroundColor: selected ? '#EEF2FF' : '#FFFFFF',
  marginBottom: '12px',
  cursor: 'pointer',
  transition: 'all 0.2s',
  '&:hover': {
    borderColor: '#4F46E5',
    backgroundColor: selected ? '#EEF2FF' : '#F9FAFB'
  }
}));

export interface PlatformDisplay {
  id: string;
  name: string;
  logo: string;
}

export interface PlatformLink {
  id: string;
  platform: string;
  url: string;
}

export interface ReviewDestinationsSectionProps {
  platforms: PlatformDisplay[];
  platformLinks: PlatformLink[];
  selectedPlatformId: string;
  onPlatformToggle: (platformId: string) => void;
  onPlatformUrlChange: (platformId: string, url: string) => void;
  onPlatformSelect: (platformId: string) => void;
  onAddCustomLink: () => void;
  onRemovePlatform: (platformId: string) => void;
}

const ReviewDestinationsSection = ({
  platforms,
  platformLinks,
  selectedPlatformId,
  onPlatformToggle,
  onPlatformUrlChange,
  onPlatformSelect,
  onAddCustomLink,
  onRemovePlatform
}: ReviewDestinationsSectionProps) => {
  return (
    <StyledCard>
      <CardContent sx={{ p: 4 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            fontSize: '1.25rem',
            letterSpacing: '-0.015em',
            mb: 0.5,
            color: '#111827',
            fontFamily: '"Inter", sans-serif'
          }}
        >
          Review Destinations
        </Typography>
        <Typography
          variant="body1"
          sx={{
            mb: 3,
            color: '#6B7280',
            fontSize: '0.875rem',
            fontFamily: '"Inter", sans-serif'
          }}
        >
          Select platforms and enter their review URLs. Choose Google, Facebook, Yelp, or add a custom URL for your company/product website.
        </Typography>

        {/* Platform Selection List */}
        <RadioGroup
          value={selectedPlatformId}
          onChange={(e) => onPlatformSelect(e.target.value)}
        >
          {platforms.map((platform) => {
            const platformLink = platformLinks.find(p => p.id === platform.id);
            const isSelected = selectedPlatformId === platform.id;
            
            return (
              <PlatformListItem
                key={platform.id}
                selected={isSelected}
                onClick={() => {
                  if (!platformLink) {
                    onPlatformToggle(platform.id);
                  }
                  onPlatformSelect(platform.id);
                }}
              >
                <Radio
                  value={platform.id}
                  checked={isSelected}
                  disabled={!platformLink}
                  sx={{
                    color: '#D1D5DB',
                    '&.Mui-checked': {
                      color: '#4F46E5'
                    },
                    '&.Mui-disabled': {
                      color: '#E5E7EB'
                    }
                  }}
                />
                <Avatar
                  src={platform.logo}
                  alt={platform.name}
                  sx={{
                    width: 32,
                    height: 32,
                    mr: 2
                  }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      color: '#111827',
                      mb: platformLink ? 1 : 0,
                      fontFamily: '"Inter", sans-serif'
                    }}
                  >
                    {platform.name}
                  </Typography>
                  {platformLink && (
                    <TextField
                      fullWidth
                      size="small"
                      placeholder={
                        platform.id === 'custom' 
                          ? 'Enter your company or product review URL' 
                          : `Enter your ${platform.name} review URL`
                      }
                      value={platformLink.url}
                      onChange={(e) => {
                        e.stopPropagation();
                        onPlatformUrlChange(platform.id, e.target.value);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                          borderRadius: '8px',
                          fontSize: '0.875rem',
                          '& fieldset': {
                            borderColor: '#D1D5DB'
                          },
                          '&:hover fieldset': {
                            borderColor: '#9CA3AF'
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#4F46E5'
                          }
                        }
                      }}
                    />
                  )}
                </Box>
                {!platformLink && (
                  <Typography
                    onClick={(e) => {
                      e.stopPropagation();
                      onPlatformToggle(platform.id);
                    }}
                    sx={{
                      fontSize: '0.75rem',
                      color: '#4F46E5',
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontFamily: '"Inter", sans-serif',
                      '&:hover': {
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    + Add
                  </Typography>
                )}
              </PlatformListItem>
            );
          })}
        </RadioGroup>
      </CardContent>
    </StyledCard>
  );
};

export default ReviewDestinationsSection;

